import cn from 'classnames';
import type {ComponentType} from 'react';

import {useGetAutoDeliveryDetailsDcx, useGetAutoDeliveryDetailsDcxStatus} from '@/api/consumerApi';
import SectionLoader from '@/components/Loader/AppLoader/SectionLoader';
import {
    AUTO_DELIVERY_DCX_ORDER_STATUS_TYPES,
    AUTO_DELIVERY_DCX_STATUS_TYPES,
} from '@/consts/icc/iccAutoDeliveryStatusTypes';

import AutoDeliveryDcxStatusActive from './HomePageAutoDeliveryDcxWidgets/AutoDeliveryDcxStatusActive';
import AutoDeliveryDcxStatusActiveNoOrder from './HomePageAutoDeliveryDcxWidgets/AutoDeliveryDcxStatusActiveNoOrder';
import AutoDeliveryDcxStatusCancelled from './HomePageAutoDeliveryDcxWidgets/AutoDeliveryDcxStatusCancelled';
import AutoDeliveryDcxStatusFailed from './HomePageAutoDeliveryDcxWidgets/AutoDeliveryDcxStatusFailed';
import AutoDeliveryDcxStatusNone from './HomePageAutoDeliveryDcxWidgets/AutoDeliveryDcxStatusNone';
import AutoDeliveryDcxStatusOnHold from './HomePageAutoDeliveryDcxWidgets/AutoDeliveryDcxStatusOnHold';
import AutoDeliveryDcxWidgetContainer from './HomePageAutoDeliveryDcxWidgets/components/AutoDeliveryDcxWidgetContainer';

type AutoDeliveryDcxWidgetProps = {
    id?: string;
    className?: string;
};

type HomePageAutoDeliveryDcxSectionProps = {
    id?: string;
    className?: string;
};

const getAutoDeliveryDcxComponent = (
    status?: string,
    orderStatus?: string
): ComponentType<AutoDeliveryDcxWidgetProps> => {
    switch (status) {
        case AUTO_DELIVERY_DCX_STATUS_TYPES.NONE:
        default:
            return AutoDeliveryDcxStatusNone;
        case AUTO_DELIVERY_DCX_STATUS_TYPES.ACTIVE:
            return orderStatus === AUTO_DELIVERY_DCX_ORDER_STATUS_TYPES.EXPECTED
                ? AutoDeliveryDcxStatusActiveNoOrder
                : AutoDeliveryDcxStatusActive;
        case AUTO_DELIVERY_DCX_STATUS_TYPES.ON_HOLD:
            return AutoDeliveryDcxStatusOnHold;
        case AUTO_DELIVERY_DCX_STATUS_TYPES.CANCELED:
            return AutoDeliveryDcxStatusCancelled;
        case AUTO_DELIVERY_DCX_STATUS_TYPES.FAILED:
            return AutoDeliveryDcxStatusFailed;
    }
};

const HomePageAutoDeliveryDcxSection = ({id, className}: HomePageAutoDeliveryDcxSectionProps) => {
    const {data} = useGetAutoDeliveryDetailsDcx();
    const {isLoading, isFetching} = useGetAutoDeliveryDetailsDcxStatus();

    if (isLoading || isFetching) {
        return (
            <AutoDeliveryDcxWidgetContainer id={id} className={cn('min-h-40', className)}>
                <SectionLoader />
            </AutoDeliveryDcxWidgetContainer>
        );
    }

    if (!data) return null;

    const {status} = data.autoDelivery;
    const {status: orderStatus} = data.order || {};

    const AutoDeliveryDcxWidgetComponent = getAutoDeliveryDcxComponent(status, orderStatus);

    if (!AutoDeliveryDcxWidgetComponent) return null;

    return <AutoDeliveryDcxWidgetComponent id={id} className={className} />;
};

export default HomePageAutoDeliveryDcxSection;
